import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import {
  BillingInterval,
  DerivedSubscription,
  PlanCode,
  TRIAL_DAYS,
  TRIAL_ELIGIBLE_PLANS,
  getPlanDefinition,
  planSupportsTrial,
  publicPlans,
  resolveDerivedSubscription,
} from "@/lib/subscriptions";
import { buildEdgeFunctionErrorMessage } from "@/lib/edgeFunctionErrors";

type SubscriptionRecord = {
  id: string;
  user_id: string;
  plan_code: PlanCode;
  billing_interval: BillingInterval;
  status: "pending" | "trial" | "active" | "expired" | "cancelled" | "past_due";
  started_at: string | null;
  current_period_end: string | null;
  payment_method: "card" | "bank_transfer" | "trial" | null;
  created_at: string;
};

function isMissingRelationError(error: unknown) {
  if (!error || typeof error !== "object") {
    return false;
  }

  const message = "message" in error ? String(error.message || "") : "";
  const code = "code" in error ? String(error.code || "") : "";
  return code === "42P01" || message.includes("account_subscriptions");
}

type TransferReceiptRecord = {
  id: string;
  subscription_id: string;
  plan_code: PlanCode;
  billing_interval: BillingInterval;
  amount: number;
  transfer_reference: string | null;
  receipt_file_name: string;
  receipt_path: string;
  status: "submitted" | "approved" | "rejected";
  created_at: string;
};

function isSubscriptionExpired(record: SubscriptionRecord) {
  if (!record.current_period_end) {
    return false;
  }

  return new Date(record.current_period_end).getTime() <= Date.now();
}

function pickCurrentSubscription(records: SubscriptionRecord[]) {
  const activeStatuses = ["active", "trial"] as const;
  const active = records.find((record) => activeStatuses.includes(record.status) && !isSubscriptionExpired(record));
  if (active) {
    return active;
  }

  return records.find((record) => record.status === "pending") || null;
}

function getMissingSubscriptionCacheKey(userId: string) {
  return `proptrust:missing-account-subscriptions:${userId}`;
}

export function useSubscription() {
  const { user, profile } = useAuth();

  const query = useQuery({
    queryKey: ["account-subscription", user?.id],
    enabled: !!user,
    queryFn: async () => {
      if (!user) {
        return [] as SubscriptionRecord[];
      }

      const cacheKey = getMissingSubscriptionCacheKey(user.id);
      if (typeof window !== "undefined" && window.sessionStorage.getItem(cacheKey) === "1") {
        return [] as SubscriptionRecord[];
      }

      const { data, error } = await supabase
        .from("account_subscriptions")
        .select("id, user_id, plan_code, billing_interval, status, started_at, current_period_end, payment_method, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) {
        if (isMissingRelationError(error)) {
          if (typeof window !== "undefined") {
            window.sessionStorage.setItem(cacheKey, "1");
          }
          return [] as SubscriptionRecord[];
        }

        throw error;
      }

      return (data || []) as SubscriptionRecord[];
    },
  });

  const receiptsQuery = useQuery({
    queryKey: ["subscription-transfer-receipts", user?.id],
    enabled: !!user,
    queryFn: async () => {
      if (!user) {
        return [] as TransferReceiptRecord[];
      }

      const { data, error } = await supabase
        .from("subscription_payment_receipts")
        .select("id, subscription_id, plan_code, billing_interval, amount, transfer_reference, receipt_file_name, receipt_path, status, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) {
        if (isMissingRelationError(error)) {
          return [] as TransferReceiptRecord[];
        }

        throw error;
      }

      return (data || []) as TransferReceiptRecord[];
    },
  });

  const currentSubscription = useMemo<DerivedSubscription>(() => {
    const current = pickCurrentSubscription(query.data || []);
    const latestHistoricalRecord = (query.data || []).find((record) => record.status !== "pending");

    if (!current && !latestHistoricalRecord && profile?.created_at) {
      const createdAt = new Date(profile.created_at);
      if (!Number.isNaN(createdAt.getTime())) {
        const trialEnds = new Date(createdAt);
        trialEnds.setDate(trialEnds.getDate() + TRIAL_DAYS);

        if (trialEnds.getTime() > Date.now()) {
          return resolveDerivedSubscription({
            activePlanCode: "starter",
            activeBillingInterval: "monthly",
            activeStatus: "trial",
            startedAt: createdAt.toISOString(),
            currentPeriodEnd: trialEnds.toISOString(),
            fallbackPlanCode: "starter",
          });
        }
      }
    }

    return resolveDerivedSubscription({
      activePlanCode: current?.plan_code,
      activeBillingInterval: current?.billing_interval,
      activeStatus: !current || current.status === "expired" || current.status === "cancelled" || current.status === "past_due" || isSubscriptionExpired(current)
        ? null
        : current.status,
      startedAt: current?.started_at,
      currentPeriodEnd: current?.current_period_end,
      fallbackPlanCode: latestHistoricalRecord?.plan_code || "starter",
    });
  }, [profile?.created_at, query.data]);

  const pendingPlan = useMemo(() => {
    const pending = (query.data || []).find((record) => record.status === "pending");
    return pending ? getPlanDefinition(pending.plan_code) : null;
  }, [query.data]);

  const latestTransferReceipt = useMemo(() => {
    return (receiptsQuery.data || [])[0] || null;
  }, [receiptsQuery.data]);

  const hasSubscriptionHistory = useMemo(() => {
    return (query.data || []).some((record) => record.status !== "pending");
  }, [query.data]);

  const isTrialEligibleForPlan = useMemo(() => {
    return (planCode: PlanCode) => !hasSubscriptionHistory && planSupportsTrial(planCode);
  }, [hasSubscriptionHistory]);

  const trialEligiblePlans = useMemo(() => {
    return TRIAL_ELIGIBLE_PLANS.filter((planCode) => isTrialEligibleForPlan(planCode));
  }, [isTrialEligibleForPlan]);

  const isRestricted = currentSubscription.status === "restricted" || currentSubscription.status === "pending";
  const hasActiveAccess = currentSubscription.status === "active" || currentSubscription.status === "trial";

  return {
    ...query,
    receiptsQuery,
    hasAuthenticatedUser: Boolean(user),
    currentSubscription,
    currentPlan: getPlanDefinition(currentSubscription.planCode),
    entitlements: currentSubscription.entitlements,
    pendingPlan,
    latestTransferReceipt,
    trialEligiblePlans,
    isTrialEligibleForPlan,
    hasSubscriptionHistory,
    isRestricted,
    hasActiveAccess,
    publicPlans,
  };
}

export function useStartPlanTrial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      planCode,
      billingInterval,
    }: {
      planCode: PlanCode;
      billingInterval: BillingInterval;
    }) => {
      const { data, error } = await supabase.functions.invoke("start-plan-trial", {
        body: {
          plan_code: planCode,
          billing_interval: billingInterval,
        },
      });

      if (error) {
        throw error;
      }

      if (!data?.subscription_id) {
        throw new Error(data?.error || "Trial could not be started.");
      }

      return data as {
        subscription_id: string;
        current_period_end: string;
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account-subscription"] });
      queryClient.invalidateQueries({ queryKey: ["subscription-transfer-receipts"] });
    },
    onError: (error) => {
      toast.error("Could not start trial", {
        description: buildEdgeFunctionErrorMessage(error, "We could not start your free trial right now. Please try again shortly."),
      });
    },
  });
}

export function usePlanCheckout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      planCode,
      billingInterval,
      callbackUrl,
    }: {
      planCode: PlanCode;
      billingInterval: BillingInterval;
      callbackUrl: string;
    }) => {
      const { data, error } = await supabase.functions.invoke("create-plan-checkout", {
        body: {
          plan_code: planCode,
          billing_interval: billingInterval,
          callback_url: callbackUrl,
        },
      });

      if (error) {
        throw error;
      }

      if (!data?.authorization_url) {
        throw new Error(data?.error || "Checkout could not be started.");
      }

      return data as {
        subscription_id: string;
        authorization_url: string;
        reference: string;
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account-subscription"] });
      queryClient.invalidateQueries({ queryKey: ["subscription-transfer-receipts"] });
    },
    onError: (error) => {
      toast.error("Could not start checkout", {
        description: buildEdgeFunctionErrorMessage(error, "We could not start checkout right now. Please try again shortly."),
      });
    },
  });
}

export function useSubmitSubscriptionTransfer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      planCode,
      billingInterval,
      amount,
      transferReference,
      receiptPath,
      receiptFileName,
      notes,
    }: {
      planCode: PlanCode;
      billingInterval: BillingInterval;
      amount: number;
      transferReference?: string;
      receiptPath: string;
      receiptFileName: string;
      notes?: string;
    }) => {
      const { data, error } = await supabase.functions.invoke("submit-subscription-transfer", {
        body: {
          plan_code: planCode,
          billing_interval: billingInterval,
          amount,
          transfer_reference: transferReference,
          receipt_path: receiptPath,
          receipt_file_name: receiptFileName,
          notes,
        },
      });

      if (error) {
        throw error;
      }

      if (!data?.receipt_id) {
        throw new Error(data?.error || "Transfer receipt could not be submitted.");
      }

      return data as {
        receipt_id: string;
        subscription_id: string;
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account-subscription"] });
      queryClient.invalidateQueries({ queryKey: ["subscription-transfer-receipts"] });
    },
  });
}
